import React, { useEffect, useState } from 'react';
import { getSeniorAgeThreshold, defaultSeniorAgeThreshold } from '../../../utils/getSeniorAgeThreshold';
import { StyleSheet, Text, TextStyle, View } from 'react-native';
import { useLocalization } from '../../../store/utils/useLocalization';
import PlusMinus2 from '../PlusMinus2/PlusMinus2';
import { Language } from '../../../types/Languages';
import { LoadingSkeleton } from '../../LoadingSkeleton';

const styles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    flexWrap: 'wrap',
  },
  inputElement: {
    paddingRight: 16,
    marginBottom: 16,
    height: 'auto',
    marginTop: 10,
  },
});

const MAX_FAMILY_MEMBER_COUNT = 8;

export type FamilyMemberCounts = {
  childFamilyMembers: number;
  adultFamilyMembers: number;
  seniorFamilyMembers: number;
};

export type HouseholdSizeProps = {
  familyMemberCounts: FamilyMemberCounts;
  state?: string;
  zipcode?: string;
  language: Language;
  labelStyle: TextStyle;
  onChange: (
    key: 'adult_family_members' | 'child_family_members' | 'senior_family_members',
    value: number
  ) => void;
};

/**
 * Inputs for household member counts (adult, child, senior)
 *
 * @note the state of the family size counts is not stored in the state of this
 * component. In this respect this component is "stateless", meaning that the
 * parent component is responsible for storing the state of the family member
 * counts
 */
function HouseholdSize(props: HouseholdSizeProps) {
  const [seniorAgeThreshold, setSeniorAgeThreshold] = useState(defaultSeniorAgeThreshold);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    // we will only display loading skeleton if resolution of senior age
    // threshold takes longer than 500ms
    const loadingDelay = setTimeout(() => setIsLoading(true), 500);

    // only update senior age threshold when state or zip code change
    // because the lookup can be expensive
    (async () => {
      setSeniorAgeThreshold(await getSeniorAgeThreshold(props.state, props.zipcode));
      clearTimeout(loadingDelay);
      setIsLoading(false);
    })();

    // clean up loading delay on unmount
    return () => clearTimeout(loadingDelay);
  }, [props.state, props.zipcode]);

  const { adultFamilyMembers, childFamilyMembers, seniorFamilyMembers } = props.familyMemberCounts;
  const { t } = useLocalization();

  const labels = {
    adultFamilyMembers: t('fragment_registration_family_textview_adults', {
      senior_age_threshold_minus_one: `${seniorAgeThreshold - 1}`,
    }),
    childFamilyMembers: t('fragment_registration_family_textview_children'),
    seniorFamilyMembers: t('fragment_registration_family_textview_seniors', {
      senior_age_threshold: `${seniorAgeThreshold}`,
    }),
  };

  const PlusMinusWithLabelSkeleton = () => (
    <View style={styles.inputElement}>
      <LoadingSkeleton style={{ width: 165, height: 15 }}></LoadingSkeleton>
      <LoadingSkeleton style={{ width: 120, height: 40, marginTop: 5 }}></LoadingSkeleton>
    </View>
  );

  return isLoading ? (
    <View style={styles.container} testID="household-size-loading-skeleton">
      <PlusMinusWithLabelSkeleton />
      <PlusMinusWithLabelSkeleton />
      <PlusMinusWithLabelSkeleton />
    </View>
  ) : (
    <View style={styles.container}>
      <View style={styles.inputElement}>
        <Text style={props.labelStyle} testID="adult-family-members-label">
          {labels.adultFamilyMembers}
        </Text>
        <PlusMinus2
          testID="adult-family-members"
          value={adultFamilyMembers}
          onChange={(newValue: number) => {
            props.onChange('adult_family_members', newValue);
          }}
          min={0}
          max={MAX_FAMILY_MEMBER_COUNT}
        />
      </View>
      <View style={styles.inputElement}>
        <Text style={props.labelStyle} testID="child-family-members-label">
          {labels.childFamilyMembers}
        </Text>
        <PlusMinus2
          testID="child-family-members"
          value={childFamilyMembers}
          onChange={(newValue: number) => {
            props.onChange('child_family_members', newValue);
          }}
          min={0}
          max={MAX_FAMILY_MEMBER_COUNT}
        />
      </View>
      <View style={styles.inputElement}>
        <Text style={props.labelStyle} testID="senior-family-members-label">
          {labels.seniorFamilyMembers}
        </Text>
        <PlusMinus2
          testID="senior-family-members"
          value={seniorFamilyMembers}
          onChange={(newValue: number) => {
            props.onChange('senior_family_members', newValue);
          }}
          min={0}
          max={MAX_FAMILY_MEMBER_COUNT}
        />
      </View>
    </View>
  );
}

export default HouseholdSize;
